import { useEffect, useState } from 'react';

/**
 * The width at which the pager can afford the current page's neighbours.
 *
 * Measured against pagination.css rather than chosen: under 768px a link is 32px wide with an 8px
 * gap, so the widest window with neighbours — previous, 1, gap, three numbers, gap, last, next —
 * is nine boxes and eight gaps, about 350px before the page's own side padding. That does not fit
 * a 320px screen, which is the overflow public-site-responsive.spec.ts guards. Without them the
 * same worst case is seven boxes, about 270px, and fits.
 *
 * Not one of the layout breakpoints in global.css (767/768, 991/992, …): those name the tiers the
 * page is laid out in, and this names how much room a row of numbers needs. 480 leaves the
 * measurement a margin rather than sitting on it.
 */
const WIDE_ENOUGH = '(min-width: 480px)';

/** Wide screens get neighbours; the decision is the width's, so it is read from the width. */
const WIDE = 1;
const NARROW = 0;

function neighboursFor(query: string): number {
  // No matchMedia means nothing is being painted — a test runner without a DOM. The narrow window
  // is the answer that fits everywhere, so it is the one to fall back to.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return NARROW;
  return window.matchMedia(query).matches ? WIDE : NARROW;
}

/**
 * How many pages either side of the current one the pager may print.
 *
 * In JavaScript rather than in CSS: hiding the extra numbers with `display: none` would leave the
 * gaps where they are, and an ellipsis standing for pages that are on the screen — or missing
 * where pages have just been hidden — says the wrong thing either way. The window has to be
 * decided before it is drawn.
 */
export function usePaginationNeighbours(): number {
  const [neighbours, setNeighbours] = useState(() => neighboursFor(WIDE_ENOUGH));

  useEffect(() => {
    const media = window.matchMedia(WIDE_ENOUGH);
    const read = () => setNeighbours(media.matches ? WIDE : NARROW);
    // Read again here, not only in the initial state: the viewport can change between the first
    // render and this effect — an orientation change, a window dragged between screens — and the
    // listener below only reports what happens after it is attached.
    read();
    media.addEventListener('change', read);
    return () => media.removeEventListener('change', read);
  }, []);

  return neighbours;
}
